<script setup lang="ts">
import { useField } from "vee-validate"
import { toRef, useTemplateRef } from "vue"
import ColorInput from "vue-color-input/dist/color-input.esm"
import { Hint } from "@/components/ui"

interface ColourPickerProps {
  modelValue?: string | Record<string, number>
  name: string
  label: string
  error?: string
  hint?: string
  format?: "rgb" | "hsv" | "hsl" | "hex" | "hex8" | "name"
}

const props = withDefaults(defineProps<ColourPickerProps>(), {
  modelValue: "",
  error: "",
  hint: "",
  format: "hex",
})

const emit = defineEmits<{ "update:modelValue": [value: string] }>()

const name = toRef(props, "name")

const { value, errorMessage, handleBlur, handleChange } = useField(name)

const picker = useTemplateRef<typeof ColorInput | null>("picker")

function updateModelValue(value: string) {
  handleChange(value)
  emit("update:modelValue", value)
}

function togglePicker() {
  if (!picker.value?.active) {
    picker.value?.pickStart()
  }
}
</script>

<template>
  <div>
    <div class="gpx-input-container" :class="{ 'gpx-input-container_error': error }">
      <label class="gpx-input-label-container" :for="name">
        <input
          :value="value || modelValue"
          :name="name"
          class="gpx-input"
          @click.stop="togglePicker"
          @focus="togglePicker"
          @input="({ target }) => updateModelValue((target as HTMLInputElement).value)"
        >
        <span
          class="gpx-floating-label"
          :class="{ 'gpx-floating-label_is-floating': modelValue || value, 'gpx-floating-label_error': error || errorMessage }"
        >
          {{ label }}
        </span>
      </label>
      <ColorInput
        ref="picker"
        :value="value"
        :model-value="value as string || modelValue"
        :format="format"
        position="left bottom"
        disable-alpha
        @update:model-value="updateModelValue"
        @blur="handleBlur"
      />
    </div>
    <span v-if="error || errorMessage" class="gpx-field-error" v-text="error || errorMessage" />
    <Hint v-if="hint" :hint="hint" />
  </div>
</template>

<style scoped>
.gpx-input {
  margin-top: 0.2rem;
}
</style>
